import { Box, SxProps, Theme, Typography, Tabs, Tab, Stack, Grid, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import BadRequestIcon from '../../../icons/Badrequesterror.svg';
import Error500 from '../../../icons/505Error.svg';
import BackIcon from '../../../icons/backIcon.svg';
import { getFetchingOwnerDetails, handleOwnerDetailsWizardGoBack, handleOwnerDetailsWizardTabChange, selectOwnersState } from '../sagas/ownersSlice';
import { useDispatch, useSelector } from 'react-redux';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import { Redirect, Route, Switch, useHistory, useLocation, useParams, useRouteMatch } from 'react-router';
import { OwnerDetailsGeneralInfo } from './OwnerDetailsGeneralInfo';
import { OwnerDetailsActivity } from './OwnerDetailsActivity';
import { OwnerDetailsProperties } from './OwnerDetailsProperties';
import { OwnerDetailsDocuments } from './OwnerDetailsDocuments';
import AddFileModal from './AddFileModal';
import Filter from '../../../ownerui/components/Filter';
import { getOwnerDocuments, selectOwnerDocumentsDataPages } from '../../../ownerui/documents/sagas/documentsSlice';
import { OwnerDetailsMaintenance } from './OwnerDetailsMaintenance';

//the order here matters
export enum OwnerDetailsWizardPageStates {
  General = 'general',
  Activity = 'activity',
  Documents = 'documents',
  Properties = 'properties',
  Maintenance = 'maintenance',
}

const tabStyle: SxProps<Theme> = {
  fontFamily: 'Work Sans',
  textTransform: 'capitalize',
  fontSize: '18px',
  fontWeight: '600',
  lineHeight: '22px',
  color: '#A8A8A8 !important',
  '&.Mui-selected': {
    color: '#000000 !important',
  },
  alignItems: 'start',
};

const useStyles = makeStyles((theme) => ({
  labelText: {
    fontFamily: 'Open Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '700 !important',
    fontSize: '18px !important',
    lineHeight: '25px !important',
    color: '#14142B',
  },
}));

/**
 * This component servers two purposes: It is the main routing engine for the Owner Details wizard (all states),
 * and it is the central component to fetch the cross-cutting owner detail information that will be shared on all the
 * tabs.
 */
export function OwnerDetailsWizard() {
  const { id: ownerUuidParam } = useParams<{ id: string }>();
  let { url } = useRouteMatch();
  const { pathname } = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useStyles();

  //local states
  const [isAddFileModalOpen, setIsAddFileModalOpen] = useState(false);

  //selectors
  const ownerDocumentsDataBook = useSelector(selectOwnerDocumentsDataPages);
  const { isFetchingOwnerDetails, isFetchingOwnerDetailsFailed, errorMessage, isUpdatingOwnerSuccess } = useSelector(selectOwnersState);
  const [currentPage, setCurrentPage] = useState(0);
  const [recordStartIndex, setRecordStartIndex] = useState(0);
  const [rangeSize, setRangeSize] = useState(100);

  /**
   * Computes the current tab the user is on
   * @returns
   */
  const computeCurrentTab = () => {
    const parts = pathname.split('/').filter(Boolean); // Split pathname into parts and remove empty parts
    const lastPartOfPath = parts[parts.length - 1]; // Get the last part of the path

    if (Object.values(OwnerDetailsWizardPageStates).includes(lastPartOfPath as OwnerDetailsWizardPageStates)) {
      // It's a valid tab
      return lastPartOfPath as OwnerDetailsWizardPageStates;
    } else {
      // Default tab
      return OwnerDetailsWizardPageStates.General;
    }
  };

  const myCurrenTab = computeCurrentTab();

  const handleTabChange = (event: React.SyntheticEvent, newValue: OwnerDetailsWizardPageStates) => {
    //to avoid unneccessary sideeffects reset states
    dispatch(handleOwnerDetailsWizardTabChange());
    history.push(`/pmui/owners/${ownerUuidParam}/${newValue.toLowerCase()}`);
  };

  const handleGoBack = () => {
    //to avoid unneccessary sideeffects reset states
    dispatch(handleOwnerDetailsWizardGoBack());
    history.push(`/pmui/owners`);
  };

  //only change the start index if we need to flag to the slice to fetch
  const setSearchPageIndex = (pageNumber: number) => {
    if (!ownerDocumentsDataBook[pageNumber]) {
      const newIndex = pageNumber * rangeSize;
      setRecordStartIndex(newIndex);
    }
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPageNumber: number) => {
    setSearchPageIndex(newPageNumber);
    setCurrentPage(newPageNumber);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentPage(0);
    setRecordStartIndex(0);
    setRangeSize(parseInt(event.target.value));
  };

  const handleUploadDocumentSuccess = () => {
    //if uploading a document is successfull fetch the documents again
    dispatch({
      type: getOwnerDocuments.type,
      payload: {
        ownerUuid: ownerUuidParam,
        ownerDocumentSearchParamsDto: {
          RangeSize: rangeSize,
          StartIndex: 0, //will be fetching from the start
        },
      },
    });
  };

  useEffect(() => {
    if (!!ownerUuidParam) {
      dispatch(getFetchingOwnerDetails(ownerUuidParam));
    }
  }, [dispatch, ownerUuidParam, isUpdatingOwnerSuccess]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }} width="100%">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #EEEEEE' }}>
        <Stack direction="row" spacing={2}>
          <Box
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={() => {
              handleGoBack();
            }}
          >
            <img
              style={{
                width: '20px',
                height: '20px',
              }}
              src={BackIcon}
              alt="backicon"
            />
            <Typography sx={{ ml: 1, color: '#39A2DB' }} className={classes.labelText}>
              {`Back`}
            </Typography>
          </Box>
          {/**Owner Documents are fetched in this filter component */}
          {myCurrenTab === OwnerDetailsWizardPageStates.Documents && <Filter rangeSize={rangeSize} startIndex={recordStartIndex} ownerUuid={ownerUuidParam} />}
        </Stack>

        {myCurrenTab === OwnerDetailsWizardPageStates.Documents && (
          <Button
            variant="contained"
            style={{
              width: '160px',
              height: '46px',
              borderRadius: '24px',
              textTransform: 'capitalize',
              color: 'white',
              backgroundColor: '#39A2DB',
              borderColor: '#39A2DB',
            }}
            onClick={() => {
              setIsAddFileModalOpen(true);
            }}
          >
            Add File
          </Button>
        )}
      </Box>
      {isFetchingOwnerDetailsFailed && String(errorMessage.statusCode)[0] === '4' ? (
        <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100%', width: '100%', margin: 0 }}>
          <Grid item display="flex" flexDirection="column" alignItems="center" sx={{ p: 2 }}>
            <Grid item display="flex" justifyContent="center" py={3}>
              <img src={BadRequestIcon} alt="400" />
            </Grid>
            <Grid item justifyContent="center">
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: '40px',
                  lineHeight: '23px',
                  color: '#2E82AF',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                {errorMessage.statusCode}
              </Typography>
            </Grid>
            <Grid mt={2} item justifyContent="center">
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '20px',
                  lineHeight: '23px',
                  color: '#9E9E9E',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                Page not found.
              </Typography>
            </Grid>
            <Grid item justifyContent="center" mt={2} xs={8}>
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '23px',
                  color: '#9E9E9E',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                {errorMessage.message.length === 0 ? '' : errorMessage.message}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      ) : isFetchingOwnerDetailsFailed && String(errorMessage.statusCode)[0] === '5' ? (
        <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100%', width: '100%', margin: 0 }}>
          <Grid item display="flex" flexDirection="column" alignItems="center" sx={{ p: 2 }}>
            <Grid item display="flex" justifyContent="center">
              <img src={Error500} alt="500" style={{ height: '35vh' }} />
            </Grid>
            <Grid item justifyContent="center">
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: '40px',
                  lineHeight: '23px',
                  color: '#2E82AF',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                {errorMessage.statusCode}
              </Typography>
            </Grid>
            <Grid mt={2} item justifyContent="center">
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '20px',
                  lineHeight: '23px',
                  color: '#9E9E9E',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                Page not found.
              </Typography>
            </Grid>
            <Grid item justifyContent="center" mt={2} xs={8}>
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '23px',
                  color: '#9E9E9E',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                {errorMessage.message.length === 0 ? '' : errorMessage.message}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      ) : isFetchingOwnerDetails ? (
        <Stack justifyContent="center" alignItems="center" style={{ height: '100%' }}>
          <SpinnerWithNoConfirmation />
        </Stack>
      ) : (
        <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex' }}>
          <Tabs
            orientation="vertical"
            variant="scrollable"
            value={myCurrenTab}
            onChange={handleTabChange}
            aria-label="Vertical tabs example"
            sx={{ pt: 2, minWidth: 140, borderRight: 1, borderColor: 'divider', '& .MuiTabs-indicator': { width: 0 } }}
          >
            <Tab label="General" {...a11yProps(0)} sx={tabStyle} value={OwnerDetailsWizardPageStates.General} />
            <Tab label="Activity" {...a11yProps(1)} sx={tabStyle} value={OwnerDetailsWizardPageStates.Activity} />
            <Tab label="Documents" {...a11yProps(2)} sx={tabStyle} value={OwnerDetailsWizardPageStates.Documents} />
            <Tab label="Properties" {...a11yProps(3)} sx={tabStyle} value={OwnerDetailsWizardPageStates.Properties} />
            <Tab label="Maintenance" {...a11yProps(3)} sx={tabStyle} value={OwnerDetailsWizardPageStates.Maintenance} />
          </Tabs>
          <Switch>
            <Route path={`${url}/${OwnerDetailsWizardPageStates.General.toLowerCase()}`}>
              <OwnerDetailsGeneralInfo ownerUuid={ownerUuidParam} />
            </Route>
            <Route path={`${url}/${OwnerDetailsWizardPageStates.Activity.toLowerCase()}`}>
              <OwnerDetailsActivity ownerUuid={ownerUuidParam} />
            </Route>
            <Route path={`${url}/${OwnerDetailsWizardPageStates.Properties.toLowerCase()}`}>
              <OwnerDetailsProperties />
            </Route>
            <Route path={`${url}/${OwnerDetailsWizardPageStates.Documents.toLowerCase()}`}>
              <OwnerDetailsDocuments ownerUuid={ownerUuidParam} rowsPerPage={rangeSize} handleChangeRowsPerPage={handleChangeRowsPerPage} page={currentPage} handleChangePage={handleChangePage} />
            </Route>
            <Route path={`${url}/${OwnerDetailsWizardPageStates.Maintenance.toLowerCase()}`}>
              <OwnerDetailsMaintenance ownerUuid={ownerUuidParam} />
            </Route>
            <Route exact path={url}>
              <Redirect to={`${url}/${OwnerDetailsWizardPageStates.General.toLowerCase()}`} />
            </Route>
          </Switch>
        </Box>
      )}
      {/*This modal is specific to the documents component and its place here is not ideal because this wizard's sole purpose is routing*/}
      {isAddFileModalOpen && <AddFileModal open={isAddFileModalOpen} closeCallback={() => setIsAddFileModalOpen(false)} ownerUuid={ownerUuidParam} onSuccessCallback={handleUploadDocumentSuccess} />}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}
